import { type Locale, SUPPORTED_LOCALES } from "@adpilot/shared";
import {
  IconKey,
  IconLanguage,
  IconLogout,
  IconMoon,
  IconUser,
  type TablerIcon,
} from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useTheme } from "next-themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChangePasswordDialog } from "@/components/account/change-password-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenuRadioGroup, DropdownMenuRadioItem } from "@/components/ui/dropdown-menu";
import {
  Menu,
  MenuGroup,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "@/components/ui/menu";
import { usePermission } from "@/hooks/use-permission";
import { api } from "@/lib/api";
import { resetDemoGeneration } from "@/lib/demo-generation";
import { persistLocale } from "@/lib/locale-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useLocaleStore } from "@/stores/locale";

const THEMES = ["system", "light", "dark"] as const;
type ThemeChoice = (typeof THEMES)[number];

const ITEM_CLASS =
  "min-h-8 gap-2 rounded-xs px-2 py-0 text-xs text-ink sm:min-h-8 sm:text-xs data-highlighted:bg-control-highlight data-highlighted:text-ink";
const SUB_TRIGGER_CLASS = `${ITEM_CLASS} data-popup-open:bg-control-highlight data-popup-open:text-ink [&>svg:last-child]:size-3.5! [&>svg:last-child]:text-muted-foreground`;
// Check sits in the trailing 20px lane, like Select and the workspace menu.
const RADIO_ITEM_CLASS =
  "min-h-8 rounded-xs pl-2 text-xs text-ink focus:bg-control-highlight focus:text-ink data-highlighted:bg-control-highlight";

export function AccountMenu(): React.ReactElement {
  const { t } = useTranslation("layout");
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const { hasModule } = usePermission();
  const locale = useLocaleStore((state) => state.locale);
  const setLocale = useLocaleStore((state) => state.setLocale);
  const { theme, setTheme } = useTheme();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const displayName = user?.name?.trim() || user?.email || t("user.fallbackName");
  const initials = toInitials(displayName);
  const themeChoice: ThemeChoice = isThemeChoice(theme) ? theme : "system";

  async function handleLogout(): Promise<void> {
    try {
      await api.post("auth/logout");
    } catch {
      // Local auth must clear even if the server session already expired.
    }
    logout();
    resetDemoGeneration();
    void navigate({ to: "/login" });
  }

  function switchLocale(nextLocale: Locale): void {
    if (nextLocale === locale) return;
    setLocale(nextLocale);
    void persistLocale(nextLocale).catch(() => undefined);
  }

  return (
    <>
      <Menu>
        <MenuTrigger
          render={
            <button
              type="button"
              aria-label={t("accountMenu.trigger")}
              className="group/account flex size-8 shrink-0 items-center justify-center rounded-full outline-none"
            />
          }
        >
          <UserAvatar
            name={displayName}
            initials={initials}
            src={user?.avatarUrl ?? null}
            className="size-7 transition-shadow duration-150 ease-out group-hover/account:shadow-[0_0_0_3px_var(--color-state-selected)] group-focus-visible/account:shadow-[0_0_0_2px_var(--color-surface),0_0_0_3px_var(--color-focus-ring),0_0_0_6px_var(--color-focus-halo)] group-data-popup-open/account:shadow-[0_0_0_3px_var(--color-state-selected)] motion-reduce:transition-none"
            fallbackClassName="text-badge"
          />
        </MenuTrigger>
        <MenuPopup side="bottom" align="end" className="w-[272px] rounded-md">
          <div className="flex items-center gap-2.5 px-2 py-2.5">
            <UserAvatar
              name={displayName}
              initials={initials}
              src={user?.avatarUrl ?? null}
              className="size-9"
              fallbackClassName="text-xs"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-px">
              <span className="truncate text-sm font-semibold text-ink">{displayName}</span>
              {user?.email && user.email !== displayName ? (
                <span className="truncate text-xs text-muted-foreground">{user.email}</span>
              ) : null}
            </div>
          </div>
          <MenuSeparator className="mx-0 my-1 bg-line" />
          <MenuGroup>
            {hasModule("settings") ? (
              <MenuItem className={ITEM_CLASS} onClick={() => void navigate({ to: "/settings" })}>
                <MenuIcon icon={IconUser} />
                <span className="min-w-0 flex-1 truncate">{t("accountMenu.accountSettings")}</span>
              </MenuItem>
            ) : null}
            <MenuItem className={ITEM_CLASS} onClick={() => setPasswordDialogOpen(true)}>
              <MenuIcon icon={IconKey} />
              <span className="min-w-0 flex-1 truncate">{t("accountMenu.changePassword")}</span>
            </MenuItem>
            <MenuSub>
              <MenuSubTrigger className={SUB_TRIGGER_CLASS}>
                <MenuIcon icon={IconLanguage} />
                <span className="min-w-0 flex-1 truncate">{t("accountMenu.language")}</span>
                <span className="shrink-0 text-muted-foreground">
                  {t(`accountMenu.languages.${locale}`)}
                </span>
              </MenuSubTrigger>
              <MenuSubPopup className="min-w-40 rounded-md">
                <DropdownMenuRadioGroup
                  value={locale}
                  onValueChange={(value) => switchLocale(value as Locale)}
                >
                  {SUPPORTED_LOCALES.map((option) => (
                    <DropdownMenuRadioItem key={option} value={option} className={RADIO_ITEM_CLASS}>
                      {t(`accountMenu.languages.${option}`)}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </MenuSubPopup>
            </MenuSub>
            <MenuSub>
              <MenuSubTrigger className={SUB_TRIGGER_CLASS}>
                <MenuIcon icon={IconMoon} />
                <span className="min-w-0 flex-1 truncate">{t("accountMenu.appearance")}</span>
                <span className="shrink-0 text-muted-foreground">
                  {t(`accountMenu.themes.${themeChoice}`)}
                </span>
              </MenuSubTrigger>
              <MenuSubPopup className="min-w-40 rounded-md">
                <DropdownMenuRadioGroup
                  value={themeChoice}
                  onValueChange={(value) => setTheme(value)}
                >
                  {THEMES.map((option) => (
                    <DropdownMenuRadioItem key={option} value={option} className={RADIO_ITEM_CLASS}>
                      {t(`accountMenu.themes.${option}`)}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </MenuSubPopup>
            </MenuSub>
          </MenuGroup>
          <MenuSeparator className="mx-0 my-1 bg-line" />
          <MenuGroup>
            <MenuItem
              variant="destructive"
              className={cn(
                ITEM_CLASS,
                "text-destructive-foreground data-highlighted:text-destructive-foreground",
              )}
              onClick={() => void handleLogout()}
            >
              <MenuIcon icon={IconLogout} className="text-destructive-foreground" />
              <span className="min-w-0 flex-1 truncate">{t("accountMenu.signOut")}</span>
            </MenuItem>
          </MenuGroup>
        </MenuPopup>
      </Menu>
      <ChangePasswordDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} />
    </>
  );
}

function MenuIcon({
  icon: Icon,
  className,
}: {
  icon: TablerIcon;
  className?: string;
}): React.ReactElement {
  return (
    <span
      className={cn(
        "flex size-4 shrink-0 items-center justify-center text-text-secondary",
        className,
      )}
    >
      <Icon className="size-[15px]" stroke={1.75} aria-hidden />
    </span>
  );
}

function UserAvatar({
  name,
  initials,
  src,
  className,
  fallbackClassName,
}: {
  name: string;
  initials: string;
  src: string | null;
  className?: string;
  fallbackClassName?: string;
}): React.ReactElement {
  return (
    <Avatar className={cn("after:hidden", className)}>
      {src ? <AvatarImage src={src} alt={name} /> : null}
      <AvatarFallback className={cn("bg-sidebar-avatar font-semibold text-ink", fallbackClassName)}>
        {initials || "AP"}
      </AvatarFallback>
    </Avatar>
  );
}

function toInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function isThemeChoice(value: string | undefined): value is ThemeChoice {
  return THEMES.some((choice) => choice === value);
}
